// Las plantillas de las generaciones del modo (spec §3.4) —
// ADR-2026-09-17-la-instruccion-por-referencia-va-en-el-prompt. Mostrar en uso ya no
// tiene plantilla: la visión escribe un prompt por foto con las buenas prácticas de
// `foto-con-modelo` (`promptDirectorEnUso`, ADR-2026-09-18-mostrar-en-uso-escribe-sus-prompts).
//
// POR QUÉ la instrucción de cada parte va en el prompt y no en un campo: el backend rotula
// las referencias con etiquetas fijas y numeradas ("Imagen de referencia N:", una por
// `adjunto` en el orden en que viajan — `etiquetaDe` en atlas-backend/services/ads/
// referencias.ts) y `priorizarReferencias` conserva el orden dentro de un tipo. Así que
// "Imagen de referencia 2: tomar el estampado" es EXACTAMENTE cómo lo recibe el motor
// (OpenAI y Seedream al pie del prompt; Gemini intercalado antes de cada imagen). Todas
// las referencias del modo viajan como `adjunto`, con la prenda PRIMERO.

import { recortarTitulo } from '../../../../chat-atlas/lienzo/tandasDelLienzo';
import { MAX_CANTIDAD } from '../asistente/directMode';
import { formatoMasParecido } from '../../replicarFoto';
import { RESULTADO_DE_MEZCLA_DEFAULT, type AccionDeDiseno, type ResultadoDeMezcla } from './disenoGuardado';

/** El tope de imágenes por pedido, el mismo del motor (Prompt libre). */
export { MAX_CANTIDAD };

/** Mismo tope que `AYUDA_DE_CAMPOS.instruccion` (`TOPES.instruccion`). */
export const TOPE_INSTRUCCION = 800;
/** `MAX_REFERENCIAS` (10) menos la prenda. */
export const MAX_PARTES = 9;

/** La cantidad POR DEFECTO de cada acción; en uso y mezcla la dejan elegir. */
export const CANTIDAD_POR_ACCION: Record<AccionDeDiseno, number> = {
  'en-uso': 4,
  mezcla: 2,
  plano: 1,
  'base-descrita': 1,
};

/** Las acciones cuya cantidad elige el usuario (ADR-2026-09-17-las-hojas-del-diseno-eligen-la-cantidad). */
export const ACCIONES_CON_CANTIDAD: readonly AccionDeDiseno[] = ['en-uso', 'mezcla'];

/**
 * La cantidad efectiva de un pedido: la pedida, acotada a [1, MAX_CANTIDAD], en las
 * acciones que la eligen; la de la acción en las demás o sin pedido.
 */
export function cantidadParaAccion(accion: AccionDeDiseno, pedida?: number): number {
  if (!ACCIONES_CON_CANTIDAD.includes(accion) || pedida === undefined || !Number.isFinite(pedida)) {
    return CANTIDAD_POR_ACCION[accion];
  }
  return Math.max(1, Math.min(MAX_CANTIDAD, Math.floor(pedida)));
}

/** `modo_utilizado` de cada acción: la analítica del backend enumera los modos. */
export const MODO_UTILIZADO_POR_ACCION: Record<AccionDeDiseno, string> = {
  'en-uso': 'diseno_en_uso',
  mezcla: 'diseno_mezcla',
  plano: 'diseno_plano',
  'base-descrita': 'diseno_base',
};

export const recortarInstruccion = (s: string): string => s.trim().slice(0, TOPE_INSTRUCCION);

/** En uso 4:5; el plano apaisado; la base 1:1; la mezcla, el más parecido a la prenda. */
export function formatoParaAccion(accion: AccionDeDiseno, ratioDeLaPrenda: number): string {
  if (accion === 'en-uso') return 'post';
  // El plano técnico dibuja FRENTE y ESPALDA una al lado de la otra: en 1:1 cada vista se
  // quedaba con menos de la mitad del ancho y los pespuntes no entraban
  // (ADR-2026-09-22-el-plano-tecnico-sale-apaisado).
  if (accion === 'plano') return 'banner';
  if (accion === 'base-descrita') return 'square';
  if (!Number.isFinite(ratioDeLaPrenda) || ratioDeLaPrenda <= 0) return 'post';
  return formatoMasParecido(ratioDeLaPrenda * 1000, 1000);
}

const ROTULO: Record<AccionDeDiseno, string> = {
  'en-uso': 'en uso',
  mezcla: 'mezcla',
  plano: 'plano técnico',
  'base-descrita': 'Base',
};

export function tituloDeTandaDeDiseno(accion: AccionDeDiseno, tituloOrigen: string): string {
  if (accion === 'base-descrita') return ROTULO[accion];
  const origen = tituloOrigen.trim();
  return recortarTitulo(origen ? `${origen} · ${ROTULO[accion]}` : ROTULO[accion]);
}

const REGLAS_DE_PLANO =
  'Plano técnico de indumentaria (flat sketch): vista de frente y vista de espalda de la prenda, una al lado de la otra, ' +
  'dibujo de línea negra limpia sobre fondo blanco puro, sin persona, sin maniquí, sin sombras ni texturas fotográficas, ' +
  'con costuras, pespuntes, cierres, bolsillos y detalles constructivos marcados. Proporciones reales de la prenda.';

/** Cuántas fotos como máximo le pide el modo a la visión de una vez (= MAX_CANTIDAD). */
const TOPE_PROMPTS_EN_USO = MAX_CANTIDAD;

/**
 * Lo que se le pide a la VISIÓN (`/v1/media/analyze`, gratis) mirando la prenda: que
 * escriba UN prompt por foto siguiendo la estructura de la skill `foto-con-modelo` del
 * agente (rol, la prenda como referencia, persona real con piel natural, pose y
 * encuadre, escena y luz, cámara, negativos), distintas entre sí. Cada prompt viaja
 * después por `creativo_plus` con la prenda como única referencia ("Imagen de
 * referencia:", tipo adjunto): la etiqueta que se cita es la que el backend le pone.
 */
export function promptDirectorEnUso(cantidad: number, instruccion = ''): string {
  const n = Math.max(1, Math.min(TOPE_PROMPTS_EN_USO, Math.floor(cantidad)));
  const extra = recortarInstruccion(instruccion);
  return [
    `Mirá la imagen adjunta: es una prenda de indumentaria (una foto, un plano técnico o un boceto). Escribí ${n} prompt${n === 1 ? '' : 's'} en castellano para un generador de imágenes, uno por foto, para mostrar a una persona real usándola. Describí primero, para vos, qué prenda es (tipo, largo, color, estampado, cuello, mangas, cierres, detalles) y usá eso en cada prompt.`,
    'Cada prompt sigue esta estructura, en este orden, en un solo párrafo de 120 a 200 palabras:',
    '1. Rol: "Fotógrafo de retratos realistas estilo street photography, especializado en texturas de piel reales y fidelidad de producto; estética natural, poros visibles, sin retoque."',
    '2. La prenda: "Viste exactamente la prenda de la imagen \"Imagen de referencia:\": mismo corte, largo, color, estampado, cuello, mangas, costuras y detalles, idénticos al original; la prenda es la protagonista, completa y nítida." Y el outfit completo hasta los pies: lo que combine con esa prenda y con la escena; si los pies entran en cuadro, calzado acorde al look (descalza sólo si la escena lo justifica: playa, pileta).',
    '3. La persona: "una persona real, fotorrealista, con piel, cara y manos naturales" (nunca un maniquí, un busto ni una percha), con género, rango de edad, contextura, tono de piel y cabello coherentes con la prenda y su público. Y la fórmula de piel, textual: "Piel natural y sana, sin retoque: se conservan poros, pecas, lunares y rojeces leves; sin acné activo, pero prohibido alisar, aerografiar o emparejar el tono. Rostro asimétrico y vivo."',
    '4. Pose y encuadre: el plano (entero, americano, medio o detalle), el ángulo, qué hace el cuerpo, los brazos y la mirada; "pose orgánica".',
    '5. Escena y luz: un lugar real y concreto con luz natural, coherente con la prenda; "sombras de contacto reales, oclusión, escala y perspectiva coherentes; grano consistente entre persona y fondo".',
    '6. Cámara: "85mm f/2.8" para retrato y medio cuerpo, "50mm" para cuerpo entero, "35mm" si el ambiente importa; "luz natural lateral o ambiental, sombras reales".',
    '7. Al final, los negativos: "Evitar: efecto collage, cara pegada, colores de la prenda alterados, logo cambiado, texto ilegible, luz plana, brillo de IA, piel plástica, maniquí, ghost mannequin, percha."',
    n > 1
      ? `Las ${n} fotos tienen que ser claramente DISTINTAS entre sí: cambiá la escena, el plano y la pose en cada una (por ejemplo cuerpo entero en la calle, plano medio en un interior luminoso, caminando, un detalle de la prenda puesta). Pueden ser la misma persona o personas distintas.`
      : 'Elegí una escena, un plano y una pose que muestren bien la prenda.',
    extra ? `Instrucción del diseñador, aplicala en todos los prompts y por encima de lo anterior si se contradicen: ${extra}` : '',
    `Devolvé SOLO un array JSON de ${n} strings (cada string es un prompt completo), sin título, sin comentarios, sin texto antes ni después.`,
  ].filter(Boolean).join('\n');
}

/**
 * Los prompts que devolvió la visión: un array JSON de strings, tolerando texto o
 * cercos de código alrededor; si no hay JSON, párrafos separados por línea en blanco o
 * líneas numeradas. Se recorta a `cantidad`; si vinieron menos, se devuelven los que hay
 * (la tanda se arma con ese total). Vacío si no se entendió nada.
 */
export function parsearPromptsDelDirector(texto: string, cantidad: number): string[] {
  const n = Math.max(1, Math.floor(cantidad));
  const limpiar = (x: unknown): string => (typeof x === 'string' ? x.replace(/\s+/g, ' ').trim() : '');
  const desde = texto.indexOf('[');
  const hasta = texto.lastIndexOf(']');
  if (desde >= 0 && hasta > desde) {
    try {
      const arr = JSON.parse(texto.slice(desde, hasta + 1)) as unknown;
      if (Array.isArray(arr)) {
        const out = arr.map(limpiar).filter((s) => s.length >= 40);
        if (out.length > 0) return out.slice(0, n);
      }
    } catch {
      /* sin JSON: se intenta por párrafos */
    }
  }
  const parrafos = texto
    .replace(/```[a-z]*/gi, '')
    .split(/\n\s*\n|\n(?=\s*\d+[.)]\s)/)
    .map((s) => limpiar(s.replace(/^\s*\d+[.)]\s*/, '').replace(/^["“]|["”]$/g, '')))
    .filter((s) => s.length >= 40);
  return parrafos.slice(0, n);
}

export function promptPlanoTecnico(): string {
  // "Imagen de referencia:" sin número: con una sola referencia el backend no numera
  // (`etiquetaDe`), y la cita tiene que ser la etiqueta literal.
  return `${REGLAS_DE_PLANO} La prenda es la de la imagen "Imagen de referencia:": respetá su corte y sus detalles.`;
}

export function promptBaseDescrita(texto: string): string {
  return `${REGLAS_DE_PLANO} La prenda: ${texto.trim()}.`;
}

/** Cuántas imágenes puede mirar la visión de una vez (`/v1/media/analyze`, `urls.max(5)`). */
export const MAX_IMAGENES_VISION = 5;

/** Una parte de la mezcla tal como se le cuenta a la visión: la instrucción del diseñador
 *  y si la imagen viaja entre las que mira (las primeras 4; el generador recibe todas). */
export interface ParteParaElDirector {
  instruccion: string;
  laVeLaVision: boolean;
  /** Lo pintado con el Marcador en esa imagen, ya contado en palabras (`describirMarca`); sin ello, toda la imagen. */
  marca?: string;
}

/** Cómo se le cuenta a la visión lo pintado con el Marcador (ADR-2026-09-21-el-marcador-de-la-mezcla). */
const PINTADO = 'una zona PINTADA con marcador ROJO semitransparente sobre la imagen que ves (la pintura no es parte de la imagen: el generador la recibe SIN ella, nunca la menciones)';

/**
 * MEZCLAR (ADR-2026-09-18-mezclar-escribe-su-prompt): lo que se le pide a la VISIÓN
 * mirando la base y las partes, con lo que el diseñador escribió al lado de cada una.
 * No hay plantilla del resultado porque una mezcla puede ser cualquier cosa —una prenda
 * nueva, la persona de otra foto usando la base, una escena—: la visión decide qué imagen
 * es el resultado y escribe UN prompt que cita cada imagen por la etiqueta exacta con la
 * que el backend la rotula ("Imagen de referencia N:", en el orden en que viajan: la
 * base primero). Las partes que la visión no ve (más de 4) se describen sólo con su
 * instrucción; el generador igual las recibe.
 */
/** Regla B con resultado `realista`: un plano técnico de base se vuelve la prenda real que describe. */
export const REGLA_B_REALISTA =
  'B. QUÉ ES LA BASE Y QUÉ SALE. El resultado es una FOTOGRAFÍA REALISTA. Si la base es un plano técnico (dibujo de línea, frente y/o espalda, sin persona, sobre blanco), el resultado NO es el plano editado: es la prenda REAL que ese plano describe —mismo corte, mismas piezas, mismas costuras y proporciones— fotografiada sola, TENDIDA PLANA sobre fondo blanco (flat lay, vista desde arriba, con la misma vista de frente que el plano), con tela, textura y costuras reales y sombras suaves de contacto, SIN maniquí ni cuerpo invisible adentro, y con lo que se toma de las partes ya aplicado; en ese caso la tarea es "crear" y "fondo_blanco" es true. Si la base es una prenda sola sobre fondo neutro, el resultado es la misma prenda, sola, con la misma vista y el mismo fondo. Si la base es una foto con persona o escena, se conserva.';
/** Regla B con resultado `como-la-base`: el mismo tipo de imagen que la base. */
export const REGLA_B_COMO_LA_BASE =
  'B. QUÉ ES LA BASE Y QUÉ SALE. El resultado es del MISMO TIPO que la base. Si es un plano técnico (dibujo de línea, frente y/o espalda, sin persona, sobre blanco), el resultado es ESE plano editado: mismo estilo de línea, mismas vistas, fondo blanco, sin persona ni escena, y lo que se toma de una parte se dibuja en el lenguaje del plano pero fiel a la referencia. Si es una prenda sola sobre fondo neutro, el resultado es la misma prenda, sola, con la misma vista y el mismo fondo.';

export function promptDirectorMezcla(
  partes: ParteParaElDirector[],
  instruccionGeneral = '',
  resultado: ResultadoDeMezcla = RESULTADO_DE_MEZCLA_DEFAULT,
  /** Lo pintado sobre la base, en palabras (`describirMarca`): dónde va el cambio. */
  marcaBase?: string,
): string {
  if (partes.length === 0) throw new Error('La mezcla no tiene partes.');
  if (partes.length > MAX_PARTES) throw new Error(`Una mezcla acepta hasta ${MAX_PARTES} partes.`);
  const lineas = partes.map((p, i) => {
    const n = i + 2;
    const que = recortarInstruccion(p.instruccion) || 'lo que la caracteriza';
    const zona = p.marca
      ? p.laVeLaVision
        ? ` ZONA MARCADA: el diseñador pintó ${PINTADO}, ${p.marca}: lo que se toma es SÓLO lo que hay bajo la pintura; el resto de la imagen se ignora.`
        : ` ZONA MARCADA: el diseñador pintó una zona en esa imagen, ${p.marca}: lo que se toma es SÓLO lo que hay en esa zona; el resto se ignora.`
      : '';
    return p.laVeLaVision
      ? `- Imagen ${n} (para el generador, "Imagen de referencia ${n}:"): tomar ${que}.${zona}`
      : `- Imagen ${n} (NO la ves, pero el generador la recibe como "Imagen de referencia ${n}:"): tomar ${que}.${zona}`;
  });
  const general = recortarInstruccion(instruccionGeneral);
  return [
    'Mirá las imágenes adjuntas. La primera es la BASE de una mezcla de diseño de indumentaria: lo que el diseñador quiere transformar. Las siguientes son las PARTES, y de cada una el diseñador dijo qué tomar:',
    ...lineas,
    marcaBase
      ? `ZONA MARCADA EN LA BASE: el diseñador pintó ${PINTADO}, ${marcaBase}. AHÍ va el cambio: lo que se toma de las partes se ubica en la zona pintada y sólo en esa, con su forma y su tamaño, y fuera de ella la base queda intacta. En el prompt traducí la zona a la anatomía de la prenda y a su tamaño relativo ("en el pecho izquierdo, a la altura del bolsillo, de unos 10 cm de ancho", "en la manga derecha, entre el codo y el puño", "en la cadera, sobre la costura lateral"): nunca coordenadas ni porcentajes, y nunca la pintura.`
      : '',
    general ? `Instrucción general del diseñador: ${general}` : '',
    'Tu trabajo es escribir el prompt para un generador de imágenes que recibe TODAS estas imágenes como referencias, con esas etiquetas. Antes de escribir, decidí dos cosas mirando las imágenes y lo pedido:',
    'A. LA TAREA. "editar" si el resultado es la imagen base con cambios: la misma prenda, la misma vista y el mismo encuadre, con lo que se toma de las partes aplicado encima. Es el caso más común (una estampa, una tela, un color, un cuello, un largo). "crear" sólo si el resultado es otra imagen: alguien de otra foto usando la base, la prenda en una escena, o una prenda nueva armada con pedazos de varias. Ante la duda, "editar".',
    resultado === 'realista' ? REGLA_B_REALISTA : REGLA_B_COMO_LA_BASE,
    'Reglas del prompt:',
    '1. Nombrá cada imagen por su etiqueta exacta: "Imagen de referencia 1:" es la base y "Imagen de referencia N:" cada parte, en ese orden. De cada una, qué se toma y qué se ignora (su fondo, la percha, el maniquí, la persona que aparece si no se pidió tomarla).',
    '2. LO QUE SE TOMA SE COPIA DE LA REFERENCIA, NO SE DESCRIBE COMO RECETA. Una estampa, una textura, una tela, un logo, un color o un detalle constructivo que viene de una parte se pide así: "la estampa de la Imagen de referencia 2, EXACTAMENTE la misma —mismo dibujo, mismos colores, mismas proporciones—, ubicada en el centro del pecho, ocupando dos tercios del ancho". Las palabras sólo identifican de qué elemento se habla y dónde va; NUNCA reemplazan a la imagen: si describís la estampa como "flores rosas sobre fondo crema" sin exigir que sea la de la referencia, el generador inventa una parecida. Decilo con "idéntica", "exacta", "calcada, no una parecida", y repetilo en el cierre del prompt. Pero se copia EL ELEMENTO, no la foto del elemento: lo idéntico es su dibujo, sus colores, sus proporciones, su material y su técnica (el punto de un bordado, la trama de una tela, el relieve de un parche); lo que se ADAPTA al destino es la perspectiva —se ve desde el ángulo de la base, no desde el ángulo en que lo muestra su foto—, la escala respecto de la prenda, cómo sigue los pliegues y la caída, y la luz y las sombras, que son las de la base. Decí las dos cosas: "el bordado de la Imagen de referencia 2, idéntico en dibujo, colores, relieve y punto, visto de frente como la prenda de la base y no desde el costado como en su foto, siguiendo la curva del pecho, con la luz de la base".',
    '3. Los detalles pesan. En una tela o textura: el tejido, el brillo, el grano y la caída de la referencia. En una estampa: su posición, escala y orientación sobre la prenda. En un color: el tono exacto de la referencia, no un nombre de color. Si el diseñador dio una medida (centímetros, dedos, talles), traducila a una proporción visible sobre la prenda ("la manga termina a mitad del antebrazo, claramente más corta que en la base") y exigí que el cambio se note a simple vista: el generador no mide, compara.',
    '4. De la base se conserva TODO lo que no se pidió cambiar —silueta, corte, construcción, color, detalles, vista, encuadre, luz, fondo—, y se dice explícitamente, nombrando la zona que cambia y sólo esa. En una edición, el resultado es la Imagen de referencia 1 tal cual, con el cambio aplicado.',
    '5. Si el resultado incluye una persona: real y fotorrealista, con piel, cara y manos naturales, y la fórmula textual "Piel natural y sana, sin retoque: se conservan poros, pecas, lunares y rojeces leves; sin acné activo, pero prohibido alisar, aerografiar o emparejar el tono. Rostro asimétrico y vivo." Si la persona viene de una parte: "la persona es la de la Imagen de referencia N: mismo rostro, cuerpo, pelo y tono de piel, re-iluminada para la escena, no pegada", y la prenda base le queda PUESTA en su talle, con caída y peso reales, con el outfit completo hasta los pies.',
    '6. Escena, luz y cámara concretas sólo cuando se crea una imagen nueva, y que sea una fotografía y no un collage: lo que se mezcla queda integrado con la misma luz, las mismas sombras y la misma escala.',
    '7. Al final, los negativos: "Evitar: efecto collage, recorte pegado, una estampa o textura parecida pero distinta de la referencia, el elemento pegado con la perspectiva o la luz de su propia foto, colores alterados de lo que se conserva, cambios fuera de la zona pedida, texto ilegible, luz plana, brillo de IA, piel plástica, maniquí, ghost mannequin, percha."',
    'Tan largo como haga falta y sin relleno: cada frase tiene que decirle al generador algo que necesita. Devolvé SOLO un objeto JSON, sin cercos de código ni comentarios: {"tarea": "editar" o "crear", "fondo_blanco": true o false, "prompt": "el prompt entero en castellano"}. "fondo_blanco" es true cuando el resultado es un plano técnico o una prenda sola cuya base ya está sobre blanco.',
  ].filter(Boolean).join('\n');
}

/** Lo que decide el director de una mezcla, además del prompt (ADR-2026-09-18-el-director-de-la-mezcla-decide-la-tarea). */
export interface SalidaDelDirectorDeMezcla {
  /** Cómo la trata el motor: "editar" conserva la base (la cadena de edición); "crear" la rehace. */
  tarea: 'editar' | 'crear';
  /** El resultado es un plano técnico o una prenda sola sobre blanco: se pide el bloque de fondo blanco. */
  fondoBlanco: boolean;
  prompt: string;
}

const PROMPT_MINIMO = 40;

/**
 * La salida del director de la mezcla: un objeto JSON `{ tarea, fondo_blanco, prompt }`,
 * tolerando cercos de código y texto alrededor. Si no vino como JSON pero hay un prompt
 * suelto, se toma con la tarea por defecto (editar: el caso más común). `null` si no hay
 * nada usable.
 */
export function parsearSalidaDelDirectorDeMezcla(texto: string): SalidaDelDirectorDeMezcla | null {
  const sinCercos = texto.replace(/```[a-z]*/gi, '').trim();
  const desde = sinCercos.indexOf('{');
  const hasta = sinCercos.lastIndexOf('}');
  if (desde !== -1 && hasta > desde) {
    let objeto: Record<string, unknown> | null = null;
    try {
      const parseado: unknown = JSON.parse(sinCercos.slice(desde, hasta + 1));
      if (parseado && typeof parseado === 'object' && !Array.isArray(parseado)) objeto = parseado as Record<string, unknown>;
    } catch {
      // JSON roto: se rescata el prompt por regex, y la tarea si se lee.
      const m = /"prompt"\s*:\s*"((?:[^"\\]|\\.)*)"/s.exec(sinCercos);
      if (m) {
        objeto = { prompt: JSON.parse(`"${m[1]}"`) as string };
        const t = /"tarea"\s*:\s*"(editar|crear)"/.exec(sinCercos);
        if (t) objeto.tarea = t[1];
        objeto.fondo_blanco = /"fondo_blanco"\s*:\s*true/.test(sinCercos);
      }
    }
    if (objeto) {
      const prompt = typeof objeto.prompt === 'string' ? objeto.prompt.replace(/\s+/g, ' ').trim() : '';
      if (prompt.length < PROMPT_MINIMO) return null;
      return {
        tarea: objeto.tarea === 'crear' ? 'crear' : 'editar',
        fondoBlanco: objeto.fondo_blanco === true,
        prompt,
      };
    }
  }
  const prompt = limpiarPromptDelDirector(texto);
  return prompt ? { tarea: 'editar', fondoBlanco: false, prompt } : null;
}

/**
 * El prompt que devolvió la visión para una mezcla: un texto suelto, tolerando cercos de
 * código, comillas alrededor o un array JSON de un elemento. Vacío si no llegó nada
 * usable.
 */
export function limpiarPromptDelDirector(texto: string): string {
  const varios = parsearPromptsDelDirector(texto, 1);
  if (texto.trimStart().startsWith('[') && varios.length > 0) return varios[0];
  const limpio = texto
    .replace(/```[a-z]*/gi, '')
    .trim()
    .replace(/^["“]+|["”]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return limpio.length >= PROMPT_MINIMO ? limpio : '';
}
